import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import {
    selectIsLoading,
    selectMezzo
} from '../../../features/Mezzi/mezziSlice';
import {
    createMezzi,
    fetchMezzo,
    singleMezzi,
    updateMezziData,
} from "../../../features/Mezzi/mezziThunk";
import {
    TextField,
    Button,
    Card,
    CardContent,
    Typography,
    IconButton,
    Box,
    Grid,
    TextareaAutosize,
    Autocomplete,
    Paper,
} from "@mui/material";
import styled from "@emotion/styled";
import { useLocation } from "react-router-dom";
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { useDispatch, useSelector } from "react-redux";
import { fetchCantines } from "../../../features/cantine/cantineThunk";
import { selectCantines } from "../../../features/cantine/cantineSlice";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import ImageIcon from '@mui/icons-material/Image';
dayjs.locale('it');

const Textarea = styled(TextareaAutosize)`
    box-sizing: border-box;
    width: 100%;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.5;
    padding: 12px;
    border-radius: 5px;
    color: #160A2A;
    background: #fff;
    border: 2px solid #e5e5e5;
`;

// Updated EmptyTextarea Component
const EmptyTextarea = ({ placeholder, value, onChange }) => {
    const [inputValue, setInputValue] = useState(value || "");

    useEffect(() => {
        setInputValue(value || "");
    }, [value]);

    const handleChange = (event) => {
        setInputValue(event.target.value);
        onChange(event);
    };

    return (
        <Textarea
            aria-label="empty textarea"
            placeholder={placeholder}
            minRows={4}
            value={inputValue}
            onChange={handleChange}
        />
    );
};

const schema = yup.object().shape({
    nomeMezzo: yup.string().required("Nome del mezzo is required"),
    tipoMezzo: yup.string().required("Tipo di mezzo is required"),
    targa: yup.string().required("Targa is required"),
    annoAcquisto: yup.date()
        .typeError("Inserisci una data valida").required("Anno di acquisto is required"),
    capienzaVolume: yup.string().required("Capienza/volume is required"),
    kmTotali: yup
        .number()
        .typeError("KM Totali must be a number")
        .required("KM Totali is required"),
    librettoCircolazione: yup.mixed(),
    manualeMezzo: yup.mixed(),
    cantina: yup.mixed(),
    
});

// File Preview Component
// File Preview Component with click-to-open functionality
const FilePreview = ({ file }) => {
    const [previewUrl, setPreviewUrl] = useState(null);
    const [fileType, setFileType] = useState(null);
    
    useEffect(() => {
        if (!file) return;
        
        // Handle URL strings (existing files from backend)
        if (typeof file === 'string') {
            setPreviewUrl(file);
            // Try to guess the file type from URL
            if (file.match(/\.(jpeg|jpg|png|gif|bmp|webp)$/i)) {
                setFileType('image');
            } else if (file.match(/\.(pdf)$/i)) {
                setFileType('pdf');
            } else if (file.match(/\.(doc|docx)$/i)) {
                setFileType('doc');
            } else if (file.match(/\.(txt|text)$/i)) {
                setFileType('text');
            } else {
                setFileType('other');
            }
            return;
        }
        
        // Handle File objects
        if (file instanceof File) {
            if (file.type.startsWith('image/')) {
                setPreviewUrl(URL.createObjectURL(file));
                setFileType('image');
            } else if (file.type === 'application/pdf') {
                setPreviewUrl(URL.createObjectURL(file));
                setFileType('pdf');
            } else if (file.type.includes('document') || file.type.includes('msword')) {
                setFileType('doc');
                setPreviewUrl(URL.createObjectURL(file));
            } else if (file.type === 'text/plain') {
                setFileType('text');
                setPreviewUrl(URL.createObjectURL(file));
            } else {
                setFileType('other');
                setPreviewUrl(URL.createObjectURL(file));
            }
        }
    }, [file]);
    
    useEffect(() => {
        // Clean up object URLs to avoid memory leaks
        return () => {
            if (previewUrl && typeof file !== 'string') {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl, file]);
    
    const openInNewTab = () => {
        if (previewUrl) {
            window.open(previewUrl, '_blank');
        }
    };
    
    if (!file) return null;
    
    return (
        <Paper elevation={1} sx={{ p: 2, mt: 1, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                {fileType === 'image' && <ImageIcon color="primary" />}
                {fileType === 'pdf' && <PictureAsPdfIcon color="error" />}
                {fileType === 'doc' && <InsertDriveFileIcon color="primary" />}
                {fileType === 'text' && <TextSnippetIcon color="info" />}
                {fileType === 'other' && <InsertDriveFileIcon color="action" />}
                
                {/* <Typography variant="body2" sx={{ ml: 1 }}>
                    {file instanceof File ? file.name : file.split('/').pop()}
                </Typography> */}
            </Box>
            
            {fileType === 'image' && previewUrl && (
                <Box 
                    sx={{ 
                        mt: 1, 
                        textAlign: 'center',
                        cursor: 'pointer',
                        '&:hover': { opacity: 0.9 }
                    }}
                    onClick={openInNewTab}
                >
                    <img 
                        src={previewUrl} 
                        alt="File Preview" 
                        style={{ maxWidth: '100%', maxHeight: '150px' }} 
                    />
                </Box>
            )}
            
            {fileType === 'pdf' && previewUrl && (
            <Box 
                sx={{ 
                mt: 1, 
                height: '150px', 
                width: '100%',
                overflow: 'hidden',
                position: 'relative',
                cursor: 'pointer',
                '&:hover': { opacity: 0.95 }
                }}
                onClick={openInNewTab}
            >
                <iframe 
                src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0`} 
                title="PDF Preview" 
                width="100%" 
                height="100%" 
                scrolling="no"
                style={{ border: 'none', overflow: 'hidden',
                    width: '100%',
                    height: '100%', }}
                />
                {/* Invisible overlay for clickable area */}
                <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 1,
                }}
                />
            </Box>
            )}

            
            {(fileType === 'doc' || fileType === 'text' || fileType === 'other') && previewUrl && (
                <Box 
                    sx={{ 
                        mt: 1, 
                        p: 2, 
                        bgcolor: '#f5f5f5', 
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#e0e0e0' }
                    }}
                    onClick={openInNewTab}
                >
                    {/* <Typography variant="body2" align="center">
                        Click to open file
                    </Typography> */}
                </Box>
            )}
            
            {(fileType === 'doc' || fileType === 'text' || fileType === 'other') && !previewUrl && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                    Preview not available for this file type
                </Typography>
            )}
        </Paper>
    );
};

const MezzoForm = ({ cantine, singleData, onClose, setCantine,cantineSingleData }) => {
    const location = useLocation();
    const dispatch = useDispatch();
    const isLoading = useSelector(selectIsLoading);
    const mezzo = useSelector(selectMezzo);
    const [openSnackbar, setOpenSnackbar] = useState(false);
  const CantineData = useSelector(selectCantines)

  const [librettoFile, setLibrettoFile] = useState(null);
  const [manualeFile, setManualeFile] = useState(null);


    const {
        control,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            nomeMezzo: "",
            tipoMezzo: "",
            targa: "",
            annoAcquisto: null,
            capienzaVolume: "",
            kmTotali: "",
            librettoCircolazione: null,
            manualeMezzo: null
        }
    });

    useEffect(() => {
        dispatch(fetchMezzo());
    dispatch(fetchCantines())

    if (singleData && CantineData.length > 0) {
        const matchedCantina =
          typeof singleData.cantina_id === 'object'
            ? singleData.cantina_id
            : CantineData.find(c => c.id === singleData.cantina_id) || null;
            
            setValue("nomeMezzo", singleData?.name || "");
            setValue("tipoMezzo", singleData?.tipo_mezzo || "");
            setValue("targa", singleData?.targa || "");
            setValue("annoAcquisto", singleData?.anno_acquisto ? dayjs(singleData.anno_acquisto) : null);
            setValue("capienzaVolume", singleData?.volume || "");
            setValue("kmTotali", singleData?.total_km || "");
            setValue("librettoCircolazione", singleData?.total_km_file || "");
            setValue("manualeMezzo", singleData?.medium_manual_file || "");
            if (matchedCantina) {
                setValue("cantina", matchedCantina);
              }
              if (singleData?.total_km_file) {
                setLibrettoFile(singleData.total_km_file);
            }
            if (singleData?.medium_manual_file) {
                setManualeFile(singleData.medium_manual_file);
            }
        }
    }, [singleData, setValue, dispatch,cantine]);

    const onSubmit = async (data) => {
        try {
          const formData = new FormData();

          // Add regular form fields
          formData.append("company_id", localStorage.getItem("company_id"));
          formData.append("name", data?.nomeMezzo);
          formData.append("tipo_mezzo", data?.tipoMezzo);
          formData.append("targa", data?.targa);
          formData.append("anno_acquisto", data?.annoAcquisto ? dayjs(data.annoAcquisto).format("YYYY-MM-DD") : "");
          formData.append("volume", data?.capienzaVolume);
          formData.append("total_km", data?.kmTotali);
          formData.append("cantina_id", localStorage.getItem("cantine_id") ||  data?.cantina?.id)

          // Handle the first file
          if (data.librettoCircolazione instanceof File) {
            // The third parameter is important - it specifies the filename
            formData.append("total_km_file", data.librettoCircolazione, data.librettoCircolazione.name);
            console.log(`Appending file: ${data.librettoCircolazione.name}, ${data.librettoCircolazione.size} bytes`);
          }
          
          if (data.manualeMezzo instanceof File) {
            formData.append("medium_manual_file", data.manualeMezzo, data.manualeMezzo.name);
            console.log(`Appending file: ${data.manualeMezzo.name}, ${data.manualeMezzo.size} bytes`);
          }
          
          // Debug logging
          console.log("FormData contents:");
          for (let [key, value] of formData.entries()) {
            if (value instanceof File) {
              console.log(`${key}: File (${value.name}, ${value.size} bytes, ${value.type})`);
            } else if (value instanceof Blob) {
              console.log(`${key}: Blob (${value.size} bytes, ${value.type})`);
            } else {
              console.log(`${key}: ${value}`);
            }
          }
          
          // Rest of your submission code...
          if (singleData) {
            dispatch(updateMezziData({ id: singleData?.id, data: formData }))
              .unwrap()
              .then(() => {
                dispatch(singleMezzi(singleData.id));
                reset();
                onClose && onClose();
              })
              .catch((err) => console.error("Update Error:", err));
          } else {
            dispatch(createMezzi(formData))
              .unwrap()
              .then(() => {
                setOpenSnackbar(true);
                setCantine(false);
                reset();
              })
              .catch((err) => console.error("Create Error:", err));
          }

          onClose();
        } catch (err) {
          console.error("Submit Error:", err);
        }
      };


    // const handleFileChange = (e, onChange) => {
    //     const file = e.target.files[0];
    //     if (file) {
    //         onChange(file);
    //     }
    // };

    const handleFileChange = (e, onChange, setFileState) => {
        const file = e.target.files[0];
        if (file) {
            onChange(file);
            setFileState(file);
        }
    };
    

    return (
        <>
            <form className="AziendaFrom" onSubmit={handleSubmit(onSubmit)}>
                <div className="AziendaFrom__inner">
                    {singleData?.cantina_id !== null && 
                      (cantineSingleData == null || Object.keys(cantineSingleData).length === 0) && (
                        <Controller
                          defaultValue={null}
                          name="cantina"
                          control={control}
                          render={({ field }) => (
                            <Autocomplete
                              options={CantineData}
                              getOptionLabel={(option) => option.name || ''}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="cantina"
                                  className="customTextBox autoCompleteTextBox"
                                  margin="normal"
                                  fullWidth
                                  error={!!errors.cantina}
                                  helperText={errors.cantina?.message}
                                />
                              )}
                              onChange={(_, value) => field.onChange(value)}
                              value={field.value || null}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                            />
                          )}
                        />
                    )}
                    <Controller
                        name="nomeMezzo"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                className="customTextBox"
                                {...field}
                                label="Nome del mezzo"
                                fullWidth
                                margin="normal"
                                error={!!errors.nomeMezzo}
                                helperText={errors.nomeMezzo?.message}
                            />
                        )}
                    />
                    <Controller
                        name="tipoMezzo"
                        control={control}
                        render={({ field }) => (
                            <Autocomplete
                                options={mezzo || []}
                                value={field.value || null}
                                onChange={(event, value) => field.onChange(value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Tipo Mezzo"
                                        className="customTextBox autoCompleteTextBox"
                                        margin="normal"
                                        fullWidth
                                        error={!!errors.tipoMezzo}
                                        helperText={errors.tipoMezzo?.message}
                                    />
                                )}
                            />
                        )}
                    />

                    <Controller
                        name="targa"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                className="customTextBox"
                                {...field}
                                label="Targa"
                                fullWidth
                                margin="normal"
                                error={!!errors.targa}
                                helperText={errors.targa?.message}
                            />
                        )}
                    />

                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                        <Controller
                            name="annoAcquisto"
                            control={control}
                            render={({ field }) => (
                                <DatePicker
                                    label="Immatricolazione"
                                    className='datePickerField'
                                    value={field.value ? dayjs(field.value) : null}
                                    onChange={(newValue) => field.onChange(newValue)}
                                />
                            )}
                        />
                    </LocalizationProvider>

                    <Controller
                        name="capienzaVolume"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                className="customTextBox"
                                {...field}
                                label="Capienza/Volume"
                                fullWidth
                                margin="normal"
                                error={!!errors.capienzaVolume}
                                helperText={errors.capienzaVolume?.message}
                            />
                        )}
                    />
                    <Controller
                        name="kmTotali"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                className="customTextBox"
                                {...field}
                                label="KM Totali"
                                fullWidth
                                margin="normal"
                                error={!!errors.kmTotali}
                                helperText={errors.kmTotali?.message}
                            />
                        )}
                    />

                    <Controller
                        name="librettoCircolazione"
                        control={control}
                        render={({ field: { onChange, value, ...field } }) => (
                            <Grid container spacing={1}>
                                <Grid item xs={8}>
                                    <TextField
                                        {...field}
                                        value={value instanceof File ? value.name : (typeof value === 'string' ? value : "No file uploaded")}
                                        className="customTextBox"
                                        label="Libretto di Circolazione"
                                        fullWidth
                                        margin="normal"
                                        InputProps={{ readOnly: true }}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <Button className="caricafile-label" variant="outlined" component="label">
                                        Carica file
                                        <input
                                            type="file"
                                            hidden
                                            onChange={(e) => handleFileChange(e, onChange, setLibrettoFile)}
                                        />
                                    </Button>
                                </Grid>
                                {/* Preview the image if it's an image file */}
                                {librettoFile && (
                                    <FilePreview file={librettoFile} />
                                )}
                            </Grid>
                        )}
                    />

                    <Controller
                        name="manualeMezzo"
                        control={control}
                        render={({ field: { onChange, value, ...field } }) => (
                            <Grid container spacing={1}>
                                <Grid size={8}>
                                    <TextField
                                        {...field}
                                        value={value instanceof File ? value.name : (typeof value === 'string' ? value : "No file uploaded")}
                                        className="customTextBox"
                                        label="Manuale Mezzo"
                                        fullWidth
                                        margin="normal"
                                        InputProps={{ readOnly: true }}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <Button className="caricafile-label" variant="outlined" component="label">
                                        Carica file
                                        <input
                                            type="file"
                                            hidden
                                            onChange={(e) => handleFileChange(e, onChange, setManualeFile)}
                                        />
                                    </Button>
                                </Grid>
                                {/* Preview the image if it's an image file */}
                                {manualeFile && (
                                    <FilePreview file={manualeFile} />
                                )}
                            </Grid>
                        )}
                    />

                    <div className="btnPart1">
                        <Button type="submit" variant="contained" disabled={isLoading}>
                            {isLoading ? "Salvataggio..." : "Salva dati"}
                        </Button>
                    </div>
                </div>
            </form>
        </> 
    );
};

export default MezzoForm;